import { useCheckPermission } from './useUserRoles';
import { useAdminCheck } from './useAdminCheck';
import { useAuth } from '@/hooks/useAuth';

// Hook to check base Specs access
export const useSpecsAccess = () => {
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  const permissionCheck = useCheckPermission('SPECS_ACCESS');
  
  return {
    data: isAdmin || permissionCheck.data,
    isLoading: isAdminLoading || permissionCheck.isLoading
  };
};

// Hook to check view permissions
export const useSpecsViewPermissions = () => {
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  const canViewItems = useCheckPermission('SPECS_ITEMS_VIEW');
  const canViewLayers = useCheckPermission('SPECS_LAYERS_VIEW');
  const canViewPins = useCheckPermission('SPECS_PINS_VIEW');
  
  if (isAdmin) {
    return {
      canViewItems: true,
      canViewLayers: true,
      canViewPins: true,
      isLoading: isAdminLoading,
    };
  }
  
  return {
    canViewItems: canViewItems.data || false,
    canViewLayers: canViewLayers.data || false,
    canViewPins: canViewPins.data || false,
    isLoading: canViewItems.isLoading || canViewLayers.isLoading || canViewPins.isLoading,
  };
};

// Hook to check edit permissions
export const useSpecsEditPermissions = () => {
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  const canEditItems = useCheckPermission('SPECS_ITEMS_EDIT');
  const canEditLayers = useCheckPermission('SPECS_LAYERS_EDIT');
  const canEditPins = useCheckPermission('SPECS_PINS_EDIT');
  
  if (isAdmin) {
    return {
      canEditItems: true,
      canEditLayers: true,
      canEditPins: true,
      isLoading: isAdminLoading,
    };
  }
  
  return {
    canEditItems: canEditItems.data || false,
    canEditLayers: canEditLayers.data || false,
    canEditPins: canEditPins.data || false,
    isLoading: canEditItems.isLoading || canEditLayers.isLoading || canEditPins.isLoading,
  };
};

// Hook to check management permissions
export const useSpecsManagePermissions = () => {
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  const canManageLayers = useCheckPermission('SPECS_MANAGE_LAYERS');
  const canUploadFiles = useCheckPermission('SPECS_UPLOAD_FILES');
  const canDeleteItems = useCheckPermission('SPECS_ITEMS_DELETE');
  const canDeletePins = useCheckPermission('SPECS_DELETE_PINS');
  const canDeleteFiles = useCheckPermission('SPECS_DELETE_FILES');
  const canExport = useCheckPermission('SPECS_EXPORT');
  
  if (isAdmin) {
    return {
      canManageLayers: true,
      canUploadFiles: true,
      canDeleteItems: true,
      canDeletePins: true,
      canDeleteFiles: true,
      canExport: true,
      isLoading: isAdminLoading,
    };
  }
  
  return {
    canManageLayers: canManageLayers.data || false,
    canUploadFiles: canUploadFiles.data || false,
    canDeleteItems: canDeleteItems.data || false,
    canDeletePins: canDeletePins.data || false,
    canDeleteFiles: canDeleteFiles.data || false,
    canExport: canExport.data || false,
    isLoading: canManageLayers.isLoading || canUploadFiles.isLoading || canDeleteItems.isLoading || canDeletePins.isLoading || canExport.isLoading || canDeleteFiles.isLoading,
  };
};

/** Gate xoá 1 file spec — MIRROR RLS "V2: Users can delete spec files with AND logic":
 *  is_admin OR uploaded_by = auth.uid() OR (SPECS_DELETE_FILES AND pup).
 *  Nhánh pup không kiểm ở FE: thiếu pup thì hook xoá throw NO_PERMISSION → toast rõ ràng, không im lặng. */
export const useSpecFileDeleteGate = () => {
  const { canDeleteFiles, isLoading } = useSpecsManagePermissions();
  const { user } = useAuth();
  // uid = id user hiện tại, lấy từ useAuth theo đúng cách các hook anh em đang dùng
  const uid = user?.id;
  const canDeleteFile = (file: { uploaded_by?: string | null }) =>
    canDeleteFiles || (!!uid && file.uploaded_by === uid);
  return { canDeleteFile, isLoading };
};